/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperBasketsV2, ShopperOrders, ShopperProducts, ShopperSearch } from '@/scapi';
import { createLogger } from '@/lib/logger';
import { type KitProductFields, hasKitRollupPrice, isKitProduct } from '@/lib/kit-utils';

const logger = createLogger();

type ProductType = ShopperProducts.schemas['ProductType'];

type Product =
    | ShopperProducts.schemas['Product']
    | (ShopperBasketsV2.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
    | (ShopperOrders.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
    | ShopperSearch.schemas['ProductSearchHit'];

// Type for product promotions - based on the commerce SDK structure
interface ProductPromotion {
    promotionalPrice?: number;
    [key: string]: unknown;
}

// Return type for findLowestPrice
interface LowestPriceResult {
    minPrice: number;
    promotion: ProductPromotion | null;
    data: Product;
}

/**
 * @private
 * Find the smallest value by key from a given array
 * @returns {Array} an array of such smallest value and the item containing this value
 * @example
 * const [value, itemContainingValue] = getSmallestValByProperty(array, key)
 */
const getSmallestValByProperty = <T extends Record<string, unknown>>(
    arr: T[] | undefined | null,
    key: keyof T
): [number, T | null] => {
    if (!arr || !arr.length) return [Infinity, null];
    if (!key) {
        throw new Error('Please specify a key.');
    }

    const filtered = arr.filter((item) => {
        const value = item[key];
        return value === 0 || value === '0' ? true : Number(value);
    });

    if (filtered.length === 0) return [Infinity, null];

    return filtered.reduce(
        (prev, item) => {
            const value = item[key];
            const [prevValue] = prev;
            const numValue = Number(value);
            return numValue < prevValue ? [numValue, item] : prev;
        },
        [Infinity, null] as [number, T | null]
    );
};

/** True for a real numeric price (including a genuine 0). */
const hasNumericPrice = (value: unknown): boolean => typeof value === 'number' && Number.isFinite(value);

/**
 * Legacy `org_ashcomm` parity (`ProductUtils.getQuantityConfig`,
 * `org_ashcomm/.../ProductUtils.js:589-600`): a product is sold as a "set of
 * multiples" — and therefore renders with the headline price multiplied up and
 * an additional `($<unit> each)` subtitle — iff the catalog's native
 * `min-order-quantity` and `step-quantity` are BOTH greater than 1 AND equal
 * to each other.
 *
 * **Source of truth chain** (first match wins):
 *
 * 1. `c_isSetOfMultiples` / `c_unitOrderQuantity` — populated by the
 *    `sfnext_ashley_hooks` SCAPI `modifyGETResponse` hooks
 *    (`SetOfMultiplesHelper`). This is the ONLY source on PLP because the
 *    SCAPI `ProductSearchHit` schema omits `minOrderQuantity` / `stepQuantity`.
 *    PDP also receives these for a uniform shape.
 * 2. Native `minOrderQuantity` / `stepQuantity` — used as a fallback in case
 *    the hook is not deployed yet or for a non-search context where the full
 *    SCAPI `Product` shape carries the values directly. Masters that lack the
 *    fields themselves fall through to the represented variant (same record
 *    `findLowestPrice` reads for the headline price).
 *
 * No site preference is involved; legacy gates entirely on these two
 * native catalog quantities and so do we.
 */
const resolveSetOfMultiples = (product: Product): { setOfMultiples: boolean; setMultiplier: number } => {
    // Source 1 — hook-decorated SCAPI response. `c_isSetOfMultiples` is a
    // boolean; when it's an explicit `false` we trust the hook (don't fall
    // through to native quantities, which may simply be unavailable on a
    // search hit and return a misleading "false").
    const decorated = product as { c_isSetOfMultiples?: boolean; c_unitOrderQuantity?: number };
    if (typeof decorated.c_isSetOfMultiples === 'boolean') {
        const unitQty = decorated.c_unitOrderQuantity;
        const isSet = decorated.c_isSetOfMultiples && typeof unitQty === 'number' && unitQty > 1;
        return {
            setOfMultiples: isSet,
            setMultiplier: isSet ? unitQty : 1,
        };
    }

    // Source 2 — native SCAPI fields (PDP `ShopperProducts.getProduct` shape).
    const direct = product as { minOrderQuantity?: number; stepQuantity?: number };
    let minQty = direct.minOrderQuantity;
    let stepQty = direct.stepQuantity;

    // Master products may carry the catalog quantities only on the represented
    // variant. Read from the same variant the price comes from so the headline
    // multiplier and the "each" subtitle always reference the same row.
    const productType = product?.type as ProductType | undefined;
    const isMaster = product?.hitType === 'master' || !!productType?.master;
    if (isMaster && (typeof minQty !== 'number' || typeof stepQty !== 'number')) {
        const represented = (product as { representedProduct?: { id?: string } }).representedProduct;
        const repId = represented?.id;
        const repVariant =
            (
                product as {
                    variants?: Array<{ productId?: string; minOrderQuantity?: number; stepQuantity?: number }>;
                }
            ).variants?.find((v) => v?.productId === repId) ?? null;
        if (repVariant) {
            if (typeof minQty !== 'number' && typeof repVariant.minOrderQuantity === 'number') {
                minQty = repVariant.minOrderQuantity;
            }
            if (typeof stepQty !== 'number' && typeof repVariant.stepQuantity === 'number') {
                stepQty = repVariant.stepQuantity;
            }
        }
    }

    const isSetOfMultiples =
        typeof minQty === 'number' && typeof stepQty === 'number' && minQty > 1 && minQty === stepQty;
    return {
        setOfMultiples: isSetOfMultiples,
        setMultiplier: isSetOfMultiples ? (minQty as number) : 1,
    };
};

/**
 * Whether ANY real price exists for this product from any source the shopper's
 * active price books could populate: a `price`, a `tieredPrices` entry, a
 * `promotionalPrice`, or — for a master — any variant with one of those.
 *
 * This is the storefront equivalent of legacy SFRA's `priceModel...isAvailable()`
 * / `Money.NOT_AVAILABLE` check. SCAPI OMITS the price fields when no applicable
 * price book has a price for the SKU (e.g. a zone whose price book has no entry),
 * so an absent price means "not purchasable in this zone" — NOT $0. Callers use
 * the negation (`priceUnavailable`) to render a "Check Price"/unavailable state
 * and to gate Add to Cart, rather than coercing the missing price to $0.00.
 */
export const productHasAnyPrice = (product: Product): boolean => {
    if (!product) return false;
    if (hasNumericPrice((product as { price?: number }).price)) return true;

    const tieredPrices = (product as { tieredPrices?: { price?: number }[] }).tieredPrices || [];
    if (tieredPrices.some((tier) => hasNumericPrice(tier.price))) return true;

    const promotions = (product as { productPromotions?: ProductPromotion[] }).productPromotions || [];
    if (promotions.some((promo) => hasNumericPrice(promo.promotionalPrice))) return true;

    const isMaster = product.hitType === 'master' || !!(product.type as ProductType | undefined)?.master;
    if (isMaster && product.variants) {
        return product.variants.some((variant) => productHasAnyPrice(variant as Product));
    }
    return false;
};

/**
 * Find the lowest price of a product, across all of its price books and promotions
 * @param {Object} product - product data from the API
 * @returns {Object|undefined} the lowest price for the given product. If it's a promotional price, the promotion will also be returned.
 */
export const findLowestPrice = (product: Product): LowestPriceResult | undefined => {
    if (!product) return undefined;

    // Look at all of the variants, only if it's a master product.
    // i.e. when a shopper has narrowed down to a variant, do not look into other variants
    const isMaster = product.hitType === 'master' || !!(product.type as ProductType | undefined)?.master;
    if (isMaster && !product.variants) {
        logger.warn(
            'Expecting `product.variants` to exist. For more accuracy, please tweak your API request to ask for the variants details.'
        );
    }
    const array = isMaster && product.variants ? product.variants : [product];

    const res = array.reduce(
        (prev, data) => {
            const promotions = (data as { productPromotions?: ProductPromotion[] }).productPromotions || [];
            const [smallestPromotionalPrice, promo] = getSmallestValByProperty(promotions, 'promotionalPrice');

            const dataPrice = data.price || 0;
            let salePrice = dataPrice;
            let promotion: ProductPromotion | null = null;

            if (smallestPromotionalPrice !== Infinity && smallestPromotionalPrice < dataPrice) {
                salePrice = smallestPromotionalPrice;
                promotion = promo;
            }

            return salePrice < prev.minPrice ? { minPrice: salePrice, promotion, data } : prev;
        },
        { minPrice: Infinity, promotion: null, data: product } as LowestPriceResult
    );

    return {
        ...res,
        // when minPrice is infinity, meaning there is no variant with lowest price found, we rest the value to 0 as minPrice to be able to use this value on UI
        minPrice: res.minPrice === Infinity ? 0 : res.minPrice,
    };
};

/**
 * Find the highest effective (selling) price among variants for a master product.
 * Used to display price range (min–max) when product.priceMax is not in the response.
 */
function findHighestPrice(product: Product): number | undefined {
    const isMaster = product?.hitType === 'master' || !!(product?.type as ProductType | undefined)?.master;
    if (!isMaster || !product?.variants?.length) return undefined;
    const array = product.variants;
    let max = -Infinity;
    for (const data of array) {
        const promotions = (data as { productPromotions?: ProductPromotion[] }).productPromotions || [];
        const [smallestPromo] = getSmallestValByProperty(promotions, 'promotionalPrice');
        const dataPrice = (data as { price?: number }).price ?? 0;
        const effectivePrice = smallestPromo !== Infinity && smallestPromo < dataPrice ? smallestPromo : dataPrice;
        if (effectivePrice > max) max = effectivePrice;
    }
    return max === -Infinity ? undefined : max;
}

/**
 * This function extract the price information of a given product
 * If a product is a master,
 *  currentPrice: get the lowest price (including promotional prices) among variants
 *  listPrice: get the list price of the variant that has lowest price (including promotional price)
 *  maxPrice: the max price in tieredPrices of variant that has lowest price
 * @param {Product} product - product detail object
 * @param {object} opts - options to pass into the function like intl, quantity, and currency
 */
export const getPriceData = (product: Product, opts: { quantity?: number } = {}) => {
    const { quantity = 1 } = opts;

    // CART-19: a kit/package master has NO price in its own price model — it is
    // priced via the roll-up of its components (KitEnrichmentHelper emits
    // c_kitSellPrice / c_kitStandardPrice). Without this branch the master would
    // fall through to `productHasAnyPrice` → priceUnavailable and render
    // "Check Price". Components in the cart are NOT kits (they price normally),
    // so this only applies to the PDP/product-GET master shape.
    const kit = product as KitProductFields;
    if (isKitProduct(kit) && hasKitRollupPrice(kit)) {
        const currentPrice = kit.c_kitSellPrice as number;
        const standardPrice = kit.c_kitStandardPrice;
        const showStandard =
            kit.c_kitShowStandardPrice === true && typeof standardPrice === 'number' && standardPrice > currentPrice;
        return {
            currentPrice,
            listPrice: showStandard ? standardPrice : undefined,
            pricePerUnit: undefined,
            isOnSale: showStandard,
            isASet: false,
            isMaster: false,
            isRange: false,
            tieredPrice: undefined,
            maxPrice: undefined,
            priceUnavailable: false,
            // Kits roll up via `c_kitSellPrice` and are never sold as catalog "set-of-N" units —
            // hard-coded false to keep the call sites uniform.
            setOfMultiples: false,
            setMultiplier: 1,
        };
    }

    // Check if this is a basket item (has basePrice and itemId - basket item specific fields)
    const isBasketItem = 'itemId' in product && 'basePrice' in product;

    if (isBasketItem) {
        // For basket items, use basket-specific price fields
        const basketItem = product as ShopperBasketsV2.schemas['ProductItem'] | ShopperOrders.schemas['ProductItem'];

        const basePrice = basketItem.basePrice ?? 0;
        const itemQuantity = basketItem.quantity ?? 1;
        // SCAPI's `ProductItem.price` is the LINE total
        // (= `basePrice` × `quantity` when no item-level discount applies, or the
        // discounted line total otherwise — same units as
        // `priceAfterItemDiscount` / `priceAfterOrderDiscount`). Bundle-component
        // edge case: when `price` is absent, fall back to `basePrice * quantity`
        // so the discounted-price math below keeps a consistent per-line scale.
        const lineGrossPrice = basketItem.price ?? basePrice * itemQuantity;
        // priceAfterItemDiscount reflects item-level adjustments only.
        // priceAfterOrderDiscount reflects item-level AND order-level adjustments (e.g. order-level coupon promos).
        // Fall through both so order-level coupon discounts are visible on line items.
        const discountedLinePrice =
            basketItem.priceAfterItemDiscount ?? basketItem.priceAfterOrderDiscount ?? lineGrossPrice;
        const currentPrice = itemQuantity > 0 ? discountedLinePrice / itemQuantity : basePrice;

        // Strike-through source is the highest PER-UNIT list price the catalog
        // carries for this SKU (the max of `tieredPrices` when present), falling
        // back to the per-unit `basePrice`. The prior implementation fell back
        // to `basketItem.price` — a LINE total — and then compared it against
        // the per-unit `currentPrice`, which falsely reported every multi-qty
        // line as "on sale" with a phantom strike at `basePrice * quantity`
        // (per-unit), then `<ProductPrice type="total">` multiplied AGAIN by
        // quantity, producing a `basePrice * quantity²` strike-through.
        const tieredPrices = product?.tieredPrices || [];
        const maxTieredPrice = tieredPrices.length
            ? Math.max(...tieredPrices.map((item) => item.price || 0))
            : undefined;
        const listPrice = maxTieredPrice && maxTieredPrice > basePrice ? maxTieredPrice : basePrice;
        const isOnSale = currentPrice < listPrice;

        // A basket line with neither a basePrice nor a price was not priced by
        // the active price books (legacy NOT_AVAILABLE). Surface it so the UI
        // shows an unavailable state instead of $0.00.
        const priceUnavailable = !hasNumericPrice(basketItem.basePrice) && !hasNumericPrice(basketItem.price);

        return {
            currentPrice,
            listPrice: isOnSale ? listPrice : undefined,
            pricePerUnit: basePrice,
            isOnSale,
            isASet: false,
            isMaster: false,
            isRange: false,
            tieredPrice: undefined,
            maxPrice: undefined,
            priceUnavailable,
            // Basket line items don't render the "($X.XX each)" subtitle in legacy
            // (`cart-line-item.isml` shows a `(Set of N)` badge next to the name
            // instead — handled elsewhere) — so we never advertise the multiplier
            // here regardless of the underlying product's catalog config.
            setOfMultiples: false,
            setMultiplier: 1,
        };
    }

    const productType = product?.type as ProductType | undefined;
    const isASet = product?.hitType === 'set' || !!productType?.set;
    const isMaster = product?.hitType === 'master' || !!productType?.master;
    let currentPrice: number;
    let variantWithLowestPrice: LowestPriceResult | null = null;

    // grab the variant that has the lowest price (including promotional price)
    if (isMaster) {
        variantWithLowestPrice = findLowestPrice(product) || null;
        currentPrice = variantWithLowestPrice?.minPrice || 0;
    } else {
        currentPrice = findLowestPrice(product)?.minPrice || 0;
    }

    // since the price is the lowest value among price books, each product will have at lease a single item tiered price at quantity 1
    // the highest value of tieredPrices is presumptively the list price
    const tieredPrices = variantWithLowestPrice?.data?.tieredPrices || product?.tieredPrices || [];
    const maxTieredPrice = tieredPrices?.length ? Math.max(...tieredPrices.map((item) => item.price || 0)) : undefined;
    const highestTieredPrice = tieredPrices.find((tier) => tier.price === maxTieredPrice);
    const listPrice = highestTieredPrice?.price;

    // if a product has tieredPrices, get the tiered that has the higher closest quantity to current quantity
    const filteredTiered = tieredPrices.filter((tiered) => (tiered.quantity || 0) <= quantity);
    const closestTieredPrice =
        filteredTiered.length &&
        filteredTiered.reduce((prev, curr) => {
            return Math.abs((curr.quantity || 0) - quantity) < Math.abs((prev.quantity || 0) - quantity) ? curr : prev;
        });

    // Check if product has a price range (priceMax is different from price)
    const hasPriceRange = product?.priceMax && product.priceMax !== product?.price;

    // Use priceMax as listPrice when there's a price range, otherwise use the calculated listPrice
    const finalListPrice = hasPriceRange ? product.priceMax : listPrice;

    // For master with multiple variants: use API priceMax when present, else compute max from variants
    const variantCount = product?.variants?.length ?? 0;
    const computedMaxForMaster =
        isMaster && variantCount > 1 && !product?.priceMax ? findHighestPrice(product) : undefined;
    const maxPrice = product?.priceMax ?? computedMaxForMaster ?? maxTieredPrice;

    const { setOfMultiples, setMultiplier } = resolveSetOfMultiples(product);

    return {
        currentPrice,
        listPrice: finalListPrice,
        pricePerUnit: product?.pricePerUnit,
        isOnSale: currentPrice < (finalListPrice || 0),
        isASet,
        isMaster,
        // For a product, set price is the lowest price of its children, so the price should be considered a range
        // For a master product, when it has more than 2 variants, we use the lowest priced variant, so it is  considered a range price
        //      but for master that has one variant, it is not considered range
        // For standard products, if priceMax is different from price, it should be considered a range
        isRange: (isMaster && variantCount > 1) || isASet || hasPriceRange || false,
        tieredPrice: closestTieredPrice && 'price' in closestTieredPrice ? closestTieredPrice.price : undefined,
        maxPrice,
        // No price from any active price book → not purchasable in this zone
        // (legacy NOT_AVAILABLE). The UI renders "Check Price"/unavailable and
        // gates Add to Cart instead of showing $0.00.
        priceUnavailable: !productHasAnyPrice(product),
        // Legacy `org_ashcomm` "set of N" gating. `currentPrice`/`listPrice` above
        // are per-unit (straight off the SCAPI/SFCC price model). Consumers that
        // want legacy parity (PLP tile, PDP) opt in via `<ProductPrice
        // showPerUnitSubtitle>` to multiply by `setMultiplier` for the headline
        // and render the per-unit "($X.XX each)" subtitle.
        setOfMultiples,
        setMultiplier,
    };
};
